import axios from "axios";

const rawBackend = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || "";

// The local development env uses the LAN address so another device can open
// the app. When the same app is opened through localhost, however, the API
// must use localhost as well: SameSite cookies otherwise are not sent from a
// localhost page to a LAN-host API.
function resolveConfiguredBackend(value) {
  if (!value || typeof window === "undefined" || value.startsWith("/")) return value;
  const browserHost = window.location.hostname;
  if (browserHost !== "localhost" && browserHost !== "127.0.0.1") return value;
  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol === "http:" && url.port === "8010") {
      url.hostname = browserHost;
      return url.toString().replace(/\/$/, "");
    }
  } catch {
    // Keep the configured value if it is not a valid absolute URL.
  }
  return value;
}

const configuredBackend = resolveConfiguredBackend(rawBackend);
const backendRoot = configuredBackend.replace(/\/api\/?$/, "");
export const API = `${backendRoot}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

function csrfToken() {
  if (typeof document === "undefined") return "";
  const match = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("csrf_token="));
  return match ? decodeURIComponent(match.slice("csrf_token=".length)) : "";
}

// The API protects every admin mutation with a double-submit CSRF token.  The
// token is intentionally readable (the auth cookie remains HttpOnly), so copy
// the current cookie into the header for every state-changing request.  This
// also picks up the new token issued by /auth/refresh.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = csrfToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

let refreshPromise = null;
const AUTH_ENDPOINTS = ["/auth/login", "/auth/refresh", "/auth/logout"];

function isAuthEndpoint(url = "") {
  return AUTH_ENDPOINTS.some((endpoint) => url.endsWith(endpoint));
}

function notifyAuthExpired() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("auth:expired"));
}

// Access tokens are short-lived. Retry one failed request after refreshing the
// session, while keeping refresh/login/logout outside the retry loop.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const url = config.url || "";

    if (status !== 401 || config._retry || config.skipAuthRefresh || isAuthEndpoint(url)) {
      if (status === 401 && url.endsWith("/auth/refresh")) notifyAuthExpired();
      return Promise.reject(error);
    }

    config._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = api
          .post("/auth/refresh", null, { skipAuthRefresh: true })
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      return api(config);
    } catch (refreshError) {
      notifyAuthExpired();
      return Promise.reject(refreshError);
    }
  }
);

export const mediaUrl = (id) => (id ? `${API}/media/${id}` : null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(", ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}
